/**
 * Forces a reload if a signed-out document comes back from the back/forward cache.
 *
 * ## Invariants:
 * - The flag lives in a module variable, not storage. Why: bfcache preserves the JS heap of
 *   the exact document it froze, so the flag is reliable and scoped to that one page — which
 *   is precisely the thing we need to detect. A storage flag would leak across documents.
 * - Only a `persisted` restore triggers the reload. Why: a normal navigation back to this URL
 *   re-runs the server render, finds no refresh cookie and renders a guest anyway; reloading
 *   that case would be a wasted round trip.
 *
 * Without this, pressing Back after an automatic sign-out can resurrect a fully-rendered
 * signed-in page from memory, with no request made and no store re-hydrated (PB-5476, AC 10).
 */

let signedOutInThisDocument = false;

export const markSignedOutInThisDocument = (): void => {
  signedOutInThisDocument = true;
};

/** Exposed for tests; resets the module flag between cases. */
export const resetSignedOutFlag = (): void => {
  signedOutInThisDocument = false;
};

export const handlePageShow = (event: PageTransitionEvent): void => {
  if (event.persisted && signedOutInThisDocument) {
    window.location.reload();
  }
};

/** Installs the listener. Returns a teardown, so callers can use it straight from an effect. */
export const installBfcacheGuard = (): (() => void) => {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener('pageshow', handlePageShow);
  return () => window.removeEventListener('pageshow', handlePageShow);
};
